import { FileIcon, Loader2Icon, PaperclipIcon, SendIcon, Trash2Icon, XIcon } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label, Skeleton } from "@/components/ui/primitives";
import { AddressField, RichTextEditor } from "./fields";
import { draftSubjectOf, type EmailDraft, formatBytes, isEmail, openAttachment, useAttachmentChange, useDiscardDraft, useEmailDraft, useSendDraft } from "./api";

const errorText = (e: unknown) => (e instanceof Error ? e.message : undefined);

/**
 * Review a client email before it goes: to, cc, subject, wording and attachments start as Pulse's defaults and can
 * all be changed. Send queues it; Discard drops it; closing keeps it as a draft (reopen it from the envelope in the
 * top bar). Nothing reaches the client until Send.
 */
export function ComposeEmailDialog({ draftId, onClose }: { draftId: string | null; onClose: () => void }) {
  const draft = useEmailDraft(draftId);
  return (
    <Dialog open={!!draftId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[92dvh] flex-col gap-3 sm:max-w-3xl" aria-describedby="compose-email-description">
        <DialogHeader>
          <DialogTitle>Review email{draft.data ? ` · ${draftSubjectOf(draft.data.entity)}` : ""}</DialogTitle>
          <DialogDescription id="compose-email-description">
            Check and change anything you need. The email goes to the client only when you press Send.
          </DialogDescription>
        </DialogHeader>
        {draft.isPending ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-56 w-full" />
          </div>
        ) : draft.isError || !draft.data ? (
          <p className="text-sm text-muted-foreground">
            This email can't be opened. {errorText(draft.error)}{" "}
            <Button variant="link" className="h-auto p-0" onClick={onClose}>
              Close
            </Button>
          </p>
        ) : (
          <ComposeForm key={draft.data.id} draft={draft.data} onClose={onClose} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ComposeForm({ draft, onClose }: { draft: EmailDraft; onClose: () => void }) {
  const ids = useId();
  const [to, setTo] = useState(draft.to);
  const [cc, setCc] = useState(draft.cc);
  const [subject, setSubject] = useState(draft.subject);
  const [body, setBody] = useState(draft.body_html);
  const [tried, setTried] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const send = useSendDraft(draft.id);
  const discard = useDiscardDraft(draft.id);
  const attach = useAttachmentChange(draft.id);
  const busy = send.isPending || discard.isPending;

  const total = draft.attachments.reduce((n, a) => n + a.size, 0);
  const badTo = to.filter((a) => !isEmail(a));
  const badCc = cc.filter((a) => !isEmail(a));
  const emptyBody = !body.replace(/<[^>]+>|&nbsp;|\s/g, "");
  const problems = [
    !to.length && "Add at least one recipient.",
    badTo.length && `To: ${badTo.join(", ")} isn't an email address.`,
    badCc.length && `Cc: ${badCc.join(", ")} isn't an email address.`,
    !subject.trim() && "Add a subject.",
    emptyBody && "The email has no content.",
    total > draft.max_attachment_bytes && `Attachments total ${formatBytes(total)}; the limit is ${formatBytes(draft.max_attachment_bytes)}.`,
  ].filter(Boolean) as string[];

  useEffect(() => {
    if (send.isSuccess) onClose();
  }, [send.isSuccess, onClose]);

  const onSend = () => {
    setTried(true);
    if (problems.length) return;
    send.mutate(
      { to, cc, subject, body_html: body },
      {
        onSuccess: (r) => toast.success("Email sent to the outbox", { description: `To ${r.to.join(", ")}` }),
        onError: (e) => toast.error("Couldn't send the email", { description: errorText(e) }),
      },
    );
  };
  const onDiscard = () => {
    if (!window.confirm("Discard this email? It won't be sent.")) return;
    discard.mutate(undefined, {
      onSuccess: () => {
        toast("Email discarded");
        onClose();
      },
      onError: (e) => toast.error("Couldn't discard the email", { description: errorText(e) }),
    });
  };
  const onFiles = (list: FileList | null) => {
    for (const file of Array.from(list ?? [])) {
      attach.mutate({ add: file }, { onError: (e) => toast.error(`Couldn't attach ${file.name}`, { description: errorText(e) }) });
    }
    if (fileInput.current) fileInput.current.value = "";
  };

  return (
    <>
      <div className="-mx-1 flex min-h-0 flex-col gap-3 overflow-y-auto px-1">
        {draft.from && (
          <p className="text-xs text-muted-foreground">
            From <span className="font-medium text-foreground">{draft.from}</span>
          </p>
        )}
        <div className="grid gap-1.5">
          <Label htmlFor={`${ids}-to`}>To</Label>
          <AddressField id={`${ids}-to`} value={to} onChange={setTo} placeholder="name@client.com" invalid={tried && (!to.length || badTo.length > 0)} />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor={`${ids}-cc`}>Cc</Label>
          <AddressField id={`${ids}-cc`} value={cc} onChange={setCc} invalid={tried && badCc.length > 0} />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor={`${ids}-subject`}>Subject</Label>
          <Input id={`${ids}-subject`} value={subject} onChange={(e) => setSubject(e.target.value)} aria-invalid={(tried && !subject.trim()) || undefined} />
        </div>
        <div className="grid gap-1.5">
          <Label id={`${ids}-body-label`} htmlFor={`${ids}-body`}>
            Message
          </Label>
          <RichTextEditor id={`${ids}-body`} docKey={draft.id} initialHtml={draft.body_html} onChange={setBody} labelledBy={`${ids}-body-label`} />
        </div>
        <section aria-label="Attachments" className="grid gap-1.5">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium">
              Attachments{" "}
              <span className="font-normal text-muted-foreground tabular">
                {formatBytes(total)} of {formatBytes(draft.max_attachment_bytes)}
              </span>
            </span>
            <Button type="button" variant="outline" size="sm" onClick={() => fileInput.current?.click()} disabled={attach.isPending}>
              {attach.isPending ? <Loader2Icon className="animate-spin" /> : <PaperclipIcon />} Attach files
            </Button>
            <input ref={fileInput} type="file" multiple className="hidden" aria-label="Attach files" onChange={(e) => onFiles(e.target.files)} />
          </div>
          {draft.attachments.length === 0 ? (
            <p className="text-xs text-muted-foreground">No attachments.</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {draft.attachments.map((a) => (
                <li key={a.id} className="flex items-center gap-2 rounded-md border px-2 py-1 text-sm">
                  <FileIcon className="size-4 text-muted-foreground" aria-hidden="true" />
                  <button type="button" className="min-w-0 flex-1 truncate text-left hover:underline" onClick={() => void openAttachment(draft.id, a.id)}>
                    {a.file_name}
                  </button>
                  <span className="text-xs text-muted-foreground tabular">{formatBytes(a.size)}</span>
                  <Button type="button" variant="ghost" size="icon-sm" aria-label={`Remove ${a.file_name}`} disabled={attach.isPending} onClick={() => attach.mutate({ remove: a.id })}>
                    <XIcon />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </section>
        {tried && problems.length > 0 && (
          <ul role="alert" className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        )}
      </div>
      <DialogFooter className="items-center sm:justify-between">
        <Button type="button" variant="ghost" className="text-destructive" onClick={onDiscard} disabled={busy}>
          <Trash2Icon /> Discard
        </Button>
        <div className="flex flex-col-reverse gap-2 sm:flex-row">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            Keep as draft
          </Button>
          <Button type="button" onClick={onSend} disabled={busy || attach.isPending}>
            {send.isPending ? <Loader2Icon className="animate-spin" /> : <SendIcon />} Send
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}
